import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { createAxiosInstance } from "api/axiosConfig";

export const useLetterAudio = ({ getLetterId, tenantVal, initialData, onSaveBefore, onTranscribed }) => {
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  const loadAudioForPlayback = useCallback(
    async (letterId) => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/patientletter/audio/${letterId}`,
          { responseType: "blob" }
        );
        const audioBlob = new Blob([response.data], { type: "audio/wav" });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
      } catch (error) {
        console.error("Error loading audio:", error);
      }
    },
    [tenantVal]
  );

  const toggleAudioPlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const uploadAudio = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "letter-audio.wav");
      formData.append("languageCode", "en-US");

      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/patientletter/scribe/${getLetterId()}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data && response.data.text) {
        onTranscribed(response.data);
        if (response.data.audioFilePath) {
          loadAudioForPlayback(response.data.id);
        }
        toast.success("Audio transcribed successfully!");
      } else {
        toast.error("Audio transcription completed but no text was returned.");
      }
    } catch (error) {
      console.error("Error uploading audio:", error);
      toast.error("Failed to process audio. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      if (!getLetterId()) {
        toast.loading("Saving letter before recording...", { id: "save-letter" });
        try {
          const success = await onSaveBefore();
          toast.dismiss("save-letter");
          if (!success || !getLetterId()) {
            toast.error("Failed to save letter. Cannot start recording.");
            return;
          }
        } catch (error) {
          toast.dismiss("save-letter");
          toast.error("Failed to save letter. Cannot start recording.");
          console.error("Error saving letter before recording:", error);
          return;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
        await uploadAudio(audioBlob);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success("Recording started");
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error("Failed to start recording. Please check microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(true);
      toast.success("Recording stopped, processing...");
    }
  };

  useEffect(() => {
    if (initialData?.audioFilePath && initialData?.id) {
      loadAudioForPlayback(initialData.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, initialData?.audioFilePath]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    audioUrl,
    setAudioUrl,
    isPlaying,
    setIsPlaying,
    audioRef,
    loadAudioForPlayback,
    toggleAudioPlayback,
  };
};
